// src/pages/ChatPage.js
//
// REESCRITO — ya no usa authStore.js/chatStore(mock). Usa chatService
// real (GET /chat/conversaciones, GET /chat/conversaciones/:id/mensajes,
// POST /chat/mensajes).
//
// DIFERENCIA DE MODELO: el mock tenía "getOrCreateConversation" (crear
// una conversación vacía de una). El backend real no tiene eso — una
// conversación se infiere del primer mensaje enviado (POST /chat/mensajes
// con destinatarioId). Por eso "Nueva conversación" acá pide directamente
// el primer mensaje, lo envía, y recién ahí aparece en la lista.
//
// Los "contactos" disponibles para iniciar chat nuevo dependen del rol:
//   - Instructor: estudiantes matriculados en sus cursos (institución)
//   - Estudiante: instructores de los cursos en los que está matriculado
//
// Soporta llegar con location.state = { targetUserId, targetUserName }
// (StudentsPage.js "Enviar mensaje" navega así) -- si ya existe una
// conversación con esa persona la selecciona, si no abre el diálogo de
// nueva conversación con ese contacto precargado.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Avatar,
  List, ListItem, IconButton, Paper, TextField, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  Breadcrumbs, Link, CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Send as SendIcon, Search as SearchIcon,
  Add as AddIcon, CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { chatService, cursoService, aprendizajeService, meService } from '../services/api';

function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [cargandoConvs, setCargandoConvs] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [cargandoMensajes, setCargandoMensajes] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  const [newChatDialog, setNewChatDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [cargandoContactos, setCargandoContactos] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newChatText, setNewChatText] = useState('');
  const [enviandoNuevo, setEnviandoNuevo] = useState(false);

  const messagesEndRef = useRef(null);

  // ── Cargar conversaciones ────────────────────────────────────────
  const cargarConversaciones = useCallback(async () => {
    setCargandoConvs(true);
    try {
      const list = await chatService.getConversaciones();
      setConversations(list);
      return list;
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las conversaciones.');
      return [];
    } finally {
      setCargandoConvs(false);
    }
  }, []);

  useEffect(() => { cargarConversaciones(); }, [cargarConversaciones]);

  // ── Si llegamos con un contacto objetivo (desde StudentsPage) ──────
  useEffect(() => {
    const target = location.state;
    if (!target?.targetUserId) return;
    (async () => {
      const list = await cargarConversaciones();
      const existente = list.find((c) => c.otherUserId === target.targetUserId);
      if (existente) {
        setSelectedConversation(existente);
      } else {
        setSelectedContact({ id: target.targetUserId, name: target.targetUserName });
        setNewChatDialog(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ── Cargar mensajes de la conversación seleccionada ────────────────
  useEffect(() => {
    if (!selectedConversation) { setMessages([]); return; }
    setCargandoMensajes(true);
    chatService.getMensajes(selectedConversation.id)
      .then((r) => setMessages(r.data))
      .catch((e) => setError(e?.message || 'No se pudieron cargar los mensajes.'))
      .finally(() => setCargandoMensajes(false));
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Cargar contactos disponibles (para "nueva conversación") ──────
  const cargarContactos = useCallback(async () => {
    setCargandoContactos(true);
    try {
      if (user?.role === 'instructor' || user?.role === 'admin') {
        const misInstituciones = await meService.getMisInstituciones();
        const institutionId = misInstituciones?.[0]?.id;
        const { data: cursos } = await cursoService.getAll(institutionId ? { institutionId, limit: 50 } : { limit: 50 });
        const mapa = new Map();
        for (const curso of cursos) {
          const { data: estudiantes } = await aprendizajeService.getEstudiantesCurso(curso.id, { limit: 200 });
          estudiantes.forEach((e) => mapa.set(e.studentId, { id: e.studentId, name: e.studentName, courseId: curso.id }));
        }
        setContacts(Array.from(mapa.values()));
      } else {
        const misCursos = await meService.getMisCursos();
        const mapa = new Map();
        misCursos.forEach((c) => {
          if (c.instructorId) mapa.set(c.instructorId, { id: c.instructorId, name: c.instructor, courseId: c.id });
        });
        setContacts(Array.from(mapa.values()));
      }
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los contactos disponibles.');
    } finally {
      setCargandoContactos(false);
    }
  }, [user?.role]);

  useEffect(() => {
    if (newChatDialog) cargarContactos();
  }, [newChatDialog, cargarContactos]);

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;
    const texto = messageText.trim();
    setMessageText('');
    try {
      await chatService.enviarMensaje(selectedConversation.otherUserId, texto, selectedConversation.courseId);
      const r = await chatService.getMensajes(selectedConversation.id);
      setMessages(r.data);
      cargarConversaciones();
    } catch (e) {
      setError(e?.message || 'No se pudo enviar el mensaje.');
    }
  };

  const handleNewChat = async () => {
    if (!selectedContact || !newChatText.trim()) return;
    setEnviandoNuevo(true);
    setError(null);
    try {
      await chatService.enviarMensaje(selectedContact.id, newChatText.trim(), selectedContact.courseId || null);
      const list = await cargarConversaciones();
      const nueva = list.find((c) => c.otherUserId === selectedContact.id);
      if (nueva) setSelectedConversation(nueva);
      setNewChatDialog(false);
      setSelectedContact(null);
      setNewChatText('');
    } catch (e) {
      setError(e?.message || 'No se pudo iniciar la conversación.');
    } finally {
      setEnviandoNuevo(false);
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { day: 'numeric', month: 'short' });
  };

  const filteredConversations = conversations.filter((conv) =>
    !searchQuery || conv.otherUserName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const backPath = user?.role === 'instructor' ? '/instructor' : '/dashboard';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(backPath); }}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            {user?.role === 'instructor' ? 'Dashboard Instructor' : 'Dashboard'}
          </Link>
          <Typography color="text.primary">Mensajes</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(backPath)}><ArrowBackIcon /></IconButton>
          <Typography variant="h4" fontWeight={700}>💬 Mensajes</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2} sx={{ height: 'calc(100vh - 260px)' }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth size="small" placeholder="Buscar conversación..."
                    value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
                  />
                  <Tooltip title="Nueva conversación">
                    <IconButton color="primary" onClick={() => setNewChatDialog(true)}
                      sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>

              <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
                {cargandoConvs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : filteredConversations.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No hay conversaciones</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNewChatDialog(true)} sx={{ mt: 2 }}>
                      Iniciar conversación
                    </Button>
                  </Box>
                ) : (
                  filteredConversations.map((conv) => (
                    <ListItem
                      key={conv.id} button
                      selected={selectedConversation?.id === conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      sx={{ borderRadius: 2, mb: 0.5, bgcolor: selectedConversation?.id === conv.id ? 'action.selected' : 'transparent' }}
                    >
                      <Avatar src={conv.otherUserAvatar} sx={{ mr: 2 }}>{conv.otherUserName?.[0]}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={conv.unread ? 700 : 500} noWrap>{conv.otherUserName}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {conv.lastMessage}
                        </Typography>
                      </Box>
                      {conv.unread > 0 && (
                        <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '50%', minWidth: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>
                          {conv.unread}
                        </Box>
                      )}
                    </ListItem>
                  ))
                )}
              </List>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {!selectedConversation ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: 'action.hover' }}>💬</Avatar>
                  <Typography variant="h6" color="text.secondary">Selecciona una conversación</Typography>
                  <Typography variant="body2" color="text.disabled">o inicia una nueva</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={selectedConversation.otherUserAvatar}>{selectedConversation.otherUserName?.[0]}</Avatar>
                    <Typography variant="subtitle1" fontWeight={600}>{selectedConversation.otherUserName}</Typography>
                  </Box>

                  <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {cargandoMensajes ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                    ) : (
                      messages.map((msg) => (
                        <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.isMine ? 'flex-end' : 'flex-start', gap: 1 }}>
                          <Paper sx={{
                            p: 1.5, px: 2, maxWidth: '70%',
                            bgcolor: msg.isMine ? 'primary.main' : 'background.paper',
                            color: msg.isMine ? 'primary.contrastText' : 'text.primary',
                            borderRadius: 2,
                            borderTopLeftRadius: !msg.isMine ? 0 : 2,
                            borderTopRightRadius: msg.isMine ? 0 : 2,
                          }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ opacity: 0.7, color: msg.isMine ? 'inherit' : 'text.secondary' }}>
                                {formatMessageTime(msg.sentAt)}
                              </Typography>
                              {msg.isMine && <CheckCircleIcon sx={{ fontSize: 14, opacity: 0.7, color: msg.read ? '#4fc3f7' : 'inherit' }} />}
                            </Box>
                          </Paper>
                        </Box>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth placeholder="Escribe un mensaje..." value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        multiline maxRows={4} size="small"
                      />
                      <IconButton color="primary" onClick={handleSendMessage} disabled={!messageText.trim()}
                        sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&:disabled': { bgcolor: 'action.disabledBackground' } }}>
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              )}
            </Card>
          </Grid>
        </Grid>

        <Dialog open={newChatDialog} onClose={() => { setNewChatDialog(false); setSelectedContact(null); setNewChatText(''); }} maxWidth="sm" fullWidth>
          <DialogTitle>Nueva Conversación</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selecciona a quién escribirle y tu primer mensaje.
            </Typography>
            {cargandoContactos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <Autocomplete
                options={contacts}
                getOptionLabel={(option) => option.name || ''}
                value={selectedContact}
                onChange={(e, value) => setSelectedContact(value)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Buscar contacto" placeholder="Escribe el nombre..." />}
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              fullWidth multiline rows={3} label="Mensaje"
              value={newChatText} onChange={(e) => setNewChatText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setNewChatDialog(false); setSelectedContact(null); setNewChatText(''); }}>Cancelar</Button>
            <Button variant="contained" onClick={handleNewChat} disabled={!selectedContact || !newChatText.trim() || enviandoNuevo}>
              {enviandoNuevo ? 'Enviando…' : 'Iniciar Chat'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default ChatPage;
